import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCampaignStore } from '../app/store/campaignStore'
import MuteluLoading from '../templates/ui/muteluloading'
import { muteluApi } from '../services/muteluApi'
import { logPageApiCall, logPageApiError, logPageApiResult } from '../utils/apiDebugLogger'

const AUTO_RECEIPT_NO = '12345678910'
const MIN_LOADING_MS = 900
const API_TIMEOUT_MS = 15000
const GEOLOCATION_TIMEOUT_MS = 6500
const RECEIPT_KEYS = ['receiptNo', 'receipt_no', 'receipt', 'ref']
const TRN_KEYS = ['trnNo', 'trn_no', 'transactionNo', 'transaction_no', 'transactionId', 'transaction_id']
const PARTNER_KEYS = ['partnerCode', 'partner_code', 'partnerId', 'partner_id', 'partner']
const CUSTOMER_KEYS = ['customer', 'customerId', 'customer_id', 'cus_id', 'cusId']
const ELEMENT_KEYS = ['element', 'elementCode', 'element_code']
const PAYLOAD_KEYS = ['payload', 'data', 'qrData', 'qr_data', 'json']
const submitFlowPromiseByQuery = new Map()
const completedSubmitFlowByQuery = new Set()
const ENABLE_SUBMIT_QUERY_DEDUPE = false
const PENDING_REWARD_POPUP_KEY = 'mutelu:pending-reward-popup'
const PENDING_DUPLICATE_RECEIPT_POPUP_KEY = 'mutelu:pending-duplicate-receipt-popup'
const PENDING_ERROR_POPUP_KEY = 'mutelu:pending-error-popup'
const PENDING_REWARD_POPUP_KEY_FALLBACK = 'mutelu:pending-reward-popup:fallback'
const PENDING_DUPLICATE_RECEIPT_POPUP_KEY_FALLBACK = 'mutelu:pending-duplicate-receipt-popup:fallback'
const PENDING_ERROR_POPUP_KEY_FALLBACK = 'mutelu:pending-error-popup:fallback'
const FORCE_TICKET_REFRESH_KEY = 'mutelu:force-ticket-refresh'
const TICKET_BALANCE_SNAPSHOT_KEY = 'mutelu:ticket-balance:snapshot'

const delay = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms))

const withTimeout = async (promiseFactory, timeoutMs) =>
  new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error('request timeout')), timeoutMs)
    Promise.resolve()
      .then(() => promiseFactory())
      .then((result) => {
        window.clearTimeout(timeoutId)
        resolve(result)
      })
      .catch((error) => {
        window.clearTimeout(timeoutId)
        reject(error)
      })
  })

const runSubmitFlowOnceByQuery = (queryKey, flowFactory) => {
  if (!ENABLE_SUBMIT_QUERY_DEDUPE) {
    return Promise.resolve().then(() => flowFactory())
  }

  if (completedSubmitFlowByQuery.has(queryKey)) {
    return Promise.resolve({ alreadyCompleted: true })
  }

  if (submitFlowPromiseByQuery.has(queryKey)) {
    return submitFlowPromiseByQuery.get(queryKey)
  }

  const flowPromise = Promise.resolve()
    .then(() => flowFactory())
    .then((result) => {
      completedSubmitFlowByQuery.add(queryKey)
      return result
    })
    .finally(() => {
      if (submitFlowPromiseByQuery.get(queryKey) === flowPromise) {
        submitFlowPromiseByQuery.delete(queryKey)
      }
    })

  submitFlowPromiseByQuery.set(queryKey, flowPromise)
  return flowPromise
}

const pickFirstNonEmpty = (source, keys) => {
  if (!source) return ''
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

const safeJsonParse = (value) => {
  if (!value || typeof value !== 'string') return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const safeBase64JsonParse = (value) => {
  if (!value || typeof value !== 'string') return null
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = atob(normalized)
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

const decodeQrPayload = (raw) => {
  if (!raw) return null

  const direct = safeJsonParse(raw)
  if (direct && typeof direct === 'object') return direct

  try {
    const parsedUrl = new URL(raw, window.location.origin)
    const params = Object.fromEntries(parsedUrl.searchParams.entries())
    if (Object.keys(params).length > 0) return params
  } catch {}

  try {
    const decoded = decodeURIComponent(raw)
    const decodedJson = safeJsonParse(decoded)
    if (decodedJson && typeof decodedJson === 'object') return decodedJson

    try {
      const parsedDecodedUrl = new URL(decoded, window.location.origin)
      const decodedParams = Object.fromEntries(parsedDecodedUrl.searchParams.entries())
      if (Object.keys(decodedParams).length > 0) return decodedParams
    } catch {}
  } catch {}

  const base64Decoded = safeBase64JsonParse(raw)
  if (base64Decoded && typeof base64Decoded === 'object') return base64Decoded

  return null
}

const toNumberOrNull = (value) => {
  if (value === '' || value == null) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const getGeoPermissionState = async () => {
  if (typeof window === 'undefined') return 'unknown'
  if (!navigator?.permissions?.query) return 'unknown'
  try {
    const permissionStatus = await navigator.permissions.query({ name: 'geolocation' })
    const state = String(permissionStatus?.state || '').trim().toLowerCase()
    return state || 'unknown'
  } catch {
    return 'unknown'
  }
}

const captureScanGeo = async () => {
  if (typeof window === 'undefined') {
    return { permission: 'unavailable' }
  }
  if (!navigator?.geolocation?.getCurrentPosition) {
    return { permission: 'unsupported' }
  }

  const currentPermission = await getGeoPermissionState()

  return new Promise((resolve) => {
    let settled = false
    const finalize = (payload) => {
      if (settled) return
      settled = true
      resolve(payload)
    }

    const timeoutId = window.setTimeout(() => {
      finalize({ permission: 'timeout' })
    }, GEOLOCATION_TIMEOUT_MS)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        window.clearTimeout(timeoutId)
        finalize({
          permission: 'granted',
          latitude: position?.coords?.latitude ?? null,
          longitude: position?.coords?.longitude ?? null,
          accuracy_m: position?.coords?.accuracy ?? null,
          captured_at: new Date(position?.timestamp || Date.now()).toISOString()
        })
      },
      (error) => {
        window.clearTimeout(timeoutId)
        const errorCode = Number(error?.code || 0)
        const fallbackPermission =
          currentPermission && currentPermission !== 'unknown'
            ? currentPermission
            : 'error'
        const permission =
          errorCode === 1
            ? 'denied'
            : errorCode === 2
              ? 'unavailable'
              : errorCode === 3
                ? 'timeout'
                : fallbackPermission
        finalize({
          permission,
          error_code: errorCode || null,
          error_message: error?.message || null
        })
      },
      {
        enableHighAccuracy: false,
        timeout: GEOLOCATION_TIMEOUT_MS - 300,
        maximumAge: 0
      }
    )
  })
}

const extractReceiptId = (payload) => {
  return (
    payload?.data?.receipt_id ||
    payload?.receipt_id ||
    payload?.data?.id ||
    payload?.id ||
    null
  )
}

const extractDataPayload = (payload) => {
  if (!payload) return null
  if (payload?.data && typeof payload.data === 'object') return payload.data
  if (typeof payload === 'object') return payload
  return null
}

const extractTicketBalancePayload = (payload) => {
  const dataPayload = extractDataPayload(payload) || {}
  const balance = dataPayload?.ticket_balance || payload?.ticket_balance
  if (!balance || typeof balance !== 'object') return null
  return balance
}

const extractTicketRewardPayloads = (payload) => {
  const dataPayload = extractDataPayload(payload) || {}
  const crossCategoryReward =
    dataPayload?.cross_category_reward ||
    payload?.cross_category_reward ||
    payload?.data?.cross_category_reward
  const normalScanReward =
    dataPayload?.ticket_reward ||
    payload?.ticket_reward ||
    payload?.data?.ticket_reward

  const toGranted = (value) =>
    value === true || String(value || '').toLowerCase() === 'true'

  const rewards = []
  if (toGranted(normalScanReward?.granted)) {
    rewards.push(normalScanReward)
  }
  if (toGranted(crossCategoryReward?.granted)) {
    rewards.push(crossCategoryReward)
  }

  const rewardPriority = {
    silver: 1,
    gold: 2,
    bronze: 3
  }

  return rewards
    .map((reward) => {
      const type = String(reward?.type || '').trim().toLowerCase()
      const amount = Number(reward?.amount || 0)
      if (!type || !Number.isFinite(amount) || amount <= 0) return null
      return {
        kind: 'scan_reward',
        ticketType: type,
        amount
      }
    })
    .filter(Boolean)
    .sort((left, right) => {
      const leftPriority = rewardPriority[left.ticketType] || 999
      const rightPriority = rewardPriority[right.ticketType] || 999
      return leftPriority - rightPriority
    })
}

const extractAlreadyScannedPayload = (payload) => {
  const dataPayload = extractDataPayload(payload) || {}
  const rewardGranted = extractTicketRewardPayloads(payload).length > 0
  if (rewardGranted) return null
  if (Boolean(dataPayload?.daily_scan_limit_reached)) {
    return {
      kind: 'daily_customer_limit',
      title: 'คุณใช้สิทธิ์สแกนวันนี้ครบแล้ว',
      message: 'สามารถกลับมาเล่นได้อีกครั้งในวันพรุ่งนี้'
    }
  }
  const alreadyScanned = Boolean(dataPayload?.already_scanned)
  if (!alreadyScanned) return null
  return {
    kind: 'already_scanned',
    message: 'คุณสามารถดูคำทำนายเดิมได้ แต่จะไม่ได้รับตั๋วเพิ่ม'
  }
}

export default function SubmitReceipt() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { state, submitReceipt, setFortuneResult } = useCampaignStore()
  const submitReceiptRef = useRef(submitReceipt)
  const setFortuneResultRef = useRef(setFortuneResult)
  const receiptNoRef = useRef(state.receiptNo)
  const pendingRewardPopupRef = useRef(null)
  const pendingDuplicatePopupRef = useRef(null)
  const pendingErrorPopupRef = useRef(null)

  useEffect(() => {
    submitReceiptRef.current = submitReceipt
  }, [submitReceipt])

  useEffect(() => {
    setFortuneResultRef.current = setFortuneResult
  }, [setFortuneResult])

  useEffect(() => {
    receiptNoRef.current = state.receiptNo
  }, [state.receiptNo])

  useEffect(() => {
    let cancelled = false
    const queryKey = searchParams.toString() || '__empty_submit_receipt_query__'

    const bootstrap = async () => {
      const startedAt = Date.now()
      let queryObject = {}
      let payloadObject = {}
      let normalizedSalePayload = null
      let nextReceipt = receiptNoRef.current || AUTO_RECEIPT_NO
      let initPlayerStatus = 'skipped'
      let submitStatus = 'skipped'
      let latestResultStatus = 'skipped'
      let receiptId = null
      let geoLocationPayload = { permission: 'unknown' }

      try {
        sessionStorage.removeItem(PENDING_REWARD_POPUP_KEY)
        sessionStorage.removeItem(PENDING_DUPLICATE_RECEIPT_POPUP_KEY)
        sessionStorage.removeItem(PENDING_ERROR_POPUP_KEY)
        localStorage.removeItem(PENDING_REWARD_POPUP_KEY_FALLBACK)
        localStorage.removeItem(PENDING_DUPLICATE_RECEIPT_POPUP_KEY_FALLBACK)
        localStorage.removeItem(PENDING_ERROR_POPUP_KEY_FALLBACK)
        pendingRewardPopupRef.current = null
        pendingDuplicatePopupRef.current = null
        pendingErrorPopupRef.current = null
        queryObject = Object.fromEntries(searchParams.entries())
        console.log('[Mutelu][Scan][RX query]', queryObject)
        const hasAnyUsefulInput =
          Boolean(pickFirstNonEmpty(queryObject, RECEIPT_KEYS)) ||
          Boolean(pickFirstNonEmpty(queryObject, PAYLOAD_KEYS))
        if (!hasAnyUsefulInput) {
          latestResultStatus = 'skipped:no-query'
          return
        }

        const payloadRaw = pickFirstNonEmpty(queryObject, PAYLOAD_KEYS)
        payloadObject = decodeQrPayload(payloadRaw) || {}
        console.log('[Mutelu][Scan][RX decoded payload]', payloadObject)
        const mergedInput = { ...payloadObject, ...queryObject }
        const fallbackReceiptFromPayload =
          typeof payloadRaw === 'string' &&
          !payloadRaw.includes('://') &&
          !payloadRaw.includes('?')
            ? payloadRaw.trim()
            : ''
        const partnerCode = pickFirstNonEmpty(mergedInput, PARTNER_KEYS)
        const customerId = pickFirstNonEmpty(mergedInput, CUSTOMER_KEYS)
        const forcedElement = pickFirstNonEmpty(mergedInput, ELEMENT_KEYS)

        normalizedSalePayload = {
          saleType: mergedInput.saleType || null,
          orderType: mergedInput.orderType || null,
          deliveryType: mergedInput.deliveryType || null,
          mediaType: mergedInput.mediaType || null,
          partnerCode: partnerCode || null,
          partner: mergedInput.partner || partnerCode || null,
          customerId: customerId || null,
          customer: mergedInput.customer || customerId || null,
          element: forcedElement || null,
          location: mergedInput.location || null,
          date: mergedInput.date || null,
          channel: mergedInput.channel || null,
          trnNo: pickFirstNonEmpty(mergedInput, TRN_KEYS) || null,
          shiftNo: mergedInput.shiftNo || null,
          inputPoints: toNumberOrNull(mergedInput.inputPoints),
          coupons: Array.isArray(mergedInput.coupons) ? mergedInput.coupons : [],
          products: Array.isArray(mergedInput.products) ? mergedInput.products : [],
          businessRules: Array.isArray(mergedInput.businessRules) ? mergedInput.businessRules : [],
          paymentMethod: mergedInput.paymentMethod || null,
          currencyCode: mergedInput.currencyCode || null,
          paymentMethods: Array.isArray(mergedInput.paymentMethods) ? mergedInput.paymentMethods : [],
          attributes: Array.isArray(mergedInput.attributes) ? mergedInput.attributes : [],
          receiptNo: pickFirstNonEmpty(mergedInput, RECEIPT_KEYS) || fallbackReceiptFromPayload || null,
          comment: mergedInput.comment || null
        }
        console.log('[Mutelu][Scan][Normalized payload]', {
          receiptNo: normalizedSalePayload?.receiptNo || null,
          trnNo: normalizedSalePayload?.trnNo || null,
          partnerCode: normalizedSalePayload?.partnerCode || null,
          customerId: normalizedSalePayload?.customerId || null,
          element: normalizedSalePayload?.element || null,
          productsCount: Array.isArray(normalizedSalePayload?.products)
            ? normalizedSalePayload.products.length
            : 0
        })

        nextReceipt = (normalizedSalePayload.receiptNo || receiptNoRef.current || '').trim() || AUTO_RECEIPT_NO
        if (nextReceipt !== receiptNoRef.current) {
          receiptNoRef.current = nextReceipt
          submitReceiptRef.current(nextReceipt)
        }

        initPlayerStatus = 'started'
        logPageApiCall('SubmitReceipt', 'muteluApi.initPlayerOnce (background)')
        withTimeout(() => muteluApi.initPlayerOnce(), API_TIMEOUT_MS)
          .then(() => {
            initPlayerStatus = 'ok'
            logPageApiResult('SubmitReceipt', 'muteluApi.initPlayerOnce (background)', { success: true })
          })
          .catch((error) => {
            initPlayerStatus = error?.message || 'failed'
            logPageApiError('SubmitReceipt', 'muteluApi.initPlayerOnce (background)', error)
          })

        try {
          geoLocationPayload = await captureScanGeo()
          logPageApiResult('SubmitReceipt', 'captureScanGeo', geoLocationPayload)
          console.log('[Mutelu][Scan][RX geolocation]', geoLocationPayload)

          const submitPayload = {
            receiptNo: nextReceipt,
            partnerCode: normalizedSalePayload?.partnerCode || null,
            trnNo: normalizedSalePayload?.trnNo || null,
            customerId: normalizedSalePayload?.customerId || null,
            element: normalizedSalePayload?.element || null,
            geo: geoLocationPayload,
            payload: normalizedSalePayload || {},
            source: 'line-qr-scan'
          }
          console.log('[Mutelu][Scan][TX /game-mutelu/receipt/submit]', {
            receiptNo: submitPayload.receiptNo,
            trnNo: submitPayload.trnNo,
            partnerCode: submitPayload.partnerCode,
            customerId: submitPayload.customerId,
            element: submitPayload.element,
            geo: submitPayload.geo || null
          })
          logPageApiCall('SubmitReceipt', 'muteluApi.submitReceipt', submitPayload)
          const submitResponse = await withTimeout(
            () => muteluApi.submitReceipt(submitPayload),
            API_TIMEOUT_MS
          )
          submitStatus = 'ok'
          logPageApiResult('SubmitReceipt', 'muteluApi.submitReceipt', submitResponse)
          console.log('[Mutelu][Scan][RX /game-mutelu/receipt/submit]', {
            receiptId: extractReceiptId(submitResponse),
            ticketReward:
              extractDataPayload(submitResponse)?.ticket_reward ||
              submitResponse?.ticket_reward ||
              null,
            crossCategoryReward:
              extractDataPayload(submitResponse)?.cross_category_reward ||
              submitResponse?.cross_category_reward ||
              null,
            clmAnalysis:
              extractDataPayload(submitResponse)?.clm_analysis ||
              submitResponse?.clm_analysis ||
              null
          })
          receiptId = extractReceiptId(submitResponse)
          const ticketBalancePayload = extractTicketBalancePayload(submitResponse)
          if (ticketBalancePayload) {
            localStorage.setItem(TICKET_BALANCE_SNAPSHOT_KEY, JSON.stringify(ticketBalancePayload))
            localStorage.setItem(FORCE_TICKET_REFRESH_KEY, String(Date.now()))
          }
          const rewardPayloads = extractTicketRewardPayloads(submitResponse)
          if (rewardPayloads.length > 0) {
            console.log('[Mutelu][SubmitReceipt] reward popup payloads', rewardPayloads)
            sessionStorage.setItem(PENDING_REWARD_POPUP_KEY, JSON.stringify(rewardPayloads))
            localStorage.setItem(PENDING_REWARD_POPUP_KEY_FALLBACK, JSON.stringify(rewardPayloads))
            localStorage.setItem(FORCE_TICKET_REFRESH_KEY, String(Date.now()))
            pendingRewardPopupRef.current = rewardPayloads
          }
          const duplicatePayload = extractAlreadyScannedPayload(submitResponse)
          if (duplicatePayload) {
            console.log('[Mutelu][SubmitReceipt] duplicate popup payload', duplicatePayload)
            sessionStorage.setItem(PENDING_DUPLICATE_RECEIPT_POPUP_KEY, JSON.stringify(duplicatePayload))
            localStorage.setItem(
              PENDING_DUPLICATE_RECEIPT_POPUP_KEY_FALLBACK,
              JSON.stringify(duplicatePayload)
            )
            pendingDuplicatePopupRef.current = duplicatePayload
          }
        } catch (error) {
          submitStatus = error?.message || 'failed'
          logPageApiError('SubmitReceipt', 'muteluApi.submitReceipt', error)
          const genericErrorPopup = {
            kind: 'submit_error',
            title: 'ทำรายการไม่สำเร็จ',
            subtitle: 'ระบบขัดข้องชั่วคราว',
            message: 'กรุณาลองอีกครั้งในภายหลัง'
          }
          sessionStorage.setItem(PENDING_ERROR_POPUP_KEY, JSON.stringify(genericErrorPopup))
          localStorage.setItem(PENDING_ERROR_POPUP_KEY_FALLBACK, JSON.stringify(genericErrorPopup))
          pendingErrorPopupRef.current = genericErrorPopup
        }

        if (receiptId || submitStatus === 'ok') {
          try {
            logPageApiCall('SubmitReceipt', 'muteluApi.getLatestReceiptResult')
            const latestResponse = await withTimeout(() => muteluApi.getLatestReceiptResult(), API_TIMEOUT_MS)
            logPageApiResult('SubmitReceipt', 'muteluApi.getLatestReceiptResult', latestResponse)
            const latestPayload = extractDataPayload(latestResponse)
            if (latestPayload) {
              setFortuneResultRef.current(latestPayload)
              latestResultStatus = 'ok'
            } else {
              latestResultStatus = 'empty'
            }
          } catch (error) {
            latestResultStatus = error?.message || 'failed'
            logPageApiError('SubmitReceipt', 'muteluApi.getLatestReceiptResult', error)
          }
        }

        logPageApiResult('SubmitReceipt', 'flow summary', {
          rawQuery: queryObject,
          decodedPayload: payloadObject,
          payloadForBackend: normalizedSalePayload,
          geolocationPayload: geoLocationPayload,
          receiptNoUsed: nextReceipt,
          partnerCodeUsed: normalizedSalePayload?.partnerCode || null,
          customerIdUsed: normalizedSalePayload?.customerId || null,
          elementUsed: normalizedSalePayload?.element || null,
          initPlayerStatus,
          submitReceiptStatus: submitStatus,
          latestResultStatus
        })
      } catch (error) {
        logPageApiError('SubmitReceipt', 'bootstrap fatal', error)
        const genericErrorPopup = {
          kind: 'submit_error',
          title: 'เกิดข้อผิดพลาด',
          subtitle: 'ไม่สามารถประมวลผลได้',
          message: 'กรุณาลองใหม่อีกครั้ง'
        }
        sessionStorage.setItem(PENDING_ERROR_POPUP_KEY, JSON.stringify(genericErrorPopup))
        localStorage.setItem(PENDING_ERROR_POPUP_KEY_FALLBACK, JSON.stringify(genericErrorPopup))
        pendingErrorPopupRef.current = genericErrorPopup
      } finally {
        const spentMs = Date.now() - startedAt
        const remainMs = MIN_LOADING_MS - spentMs
        if (remainMs > 0) {
          await delay(remainMs)
        }
      }
    }

    runSubmitFlowOnceByQuery(queryKey, bootstrap)
      .then((result) => {
        if (result?.alreadyCompleted) {
          const duplicatePayload = {
            kind: 'already_scanned',
            message: 'คุณสามารถดูคำทำนายเดิมได้ แต่จะไม่ได้รับตั๋วเพิ่ม'
          }
          sessionStorage.setItem(PENDING_DUPLICATE_RECEIPT_POPUP_KEY, JSON.stringify(duplicatePayload))
          localStorage.setItem(
            PENDING_DUPLICATE_RECEIPT_POPUP_KEY_FALLBACK,
            JSON.stringify(duplicatePayload)
          )
          pendingDuplicatePopupRef.current = duplicatePayload
        }
      })
      .catch((error) => {
        logPageApiError('SubmitReceipt', 'runSubmitFlowOnceByQuery', error)
      })
      .finally(() => {
        if (cancelled) return
        navigate('/game/mutelu/receipt', {
          replace: true,
          state: {
            pendingRewardPopup: pendingRewardPopupRef.current || null,
            pendingDuplicatePopup: pendingDuplicatePopupRef.current || null,
            pendingErrorPopup: pendingErrorPopupRef.current || null
          }
        })
      })

    return () => {
      cancelled = true
    }
  }, [navigate, searchParams])

  return <MuteluLoading />
}
